// directDebitSammelButton.js - Adds "Sammel-Lastschrift" button to direct debit list header and dialog to merge selected debits.

let Lux_DirectDebitSammel_observer = null
const LUX_SAMMEL_BUTTON_ID = 'lux-direct-debit-sammel-btn'

/** Material Design link (chain) icon - 24 viewBox, scales to 20px */
const CHAIN_LINK_ICON_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>'

/**
 * Get token and domainId from localStorage auth (same pattern as other injects).
 * Both are required for Sammel/Archiv; returns null if either is missing or invalid.
 * @returns {{ token: string, domainId: number } | null}
 */
function getAuth() {
  try {
    const authData = localStorage.getItem('auth')
    if (!authData) return null
    const parsed = JSON.parse(authData)
    const token = parsed && parsed.token
    if (!token || typeof token !== 'string') return null
    const payload = token.split('.')[1]
    if (!payload) return null
    const decoded = JSON.parse(atob(payload))
    const id = decoded.domainId != null ? Number(decoded.domainId) : null
    if (!Number.isInteger(id) || id <= 0) return null
    return { token, domainId: id }
  } catch (e) {
    return null
  }
}

/**
 * Fetch one page of direct debits (same /services/ filter as existing frontend).
 */
async function fetchDirectDebitsPage(token, maxExecutionDate, order, sort, page) {
  const query = new URLSearchParams({
    maxExecutionDate,
    order,
    page: String(page),
    sort,
    states: 'NEW'
  }).toString()
  const url = `/services/pmp-accounting/api/v1/direct-debits/filter?${query}`
  const res = await fetch(url, {
    method: 'GET',
    headers: { accept: '*/*', Authorization: `Bearer ${token}` }
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = (data && (data.message || data.error)) ? (data.message || data.error) : `Filter failed: ${res.status}`
    throw new Error(msg)
  }
  return data
}

/**
 * Fetch ALL pages of direct debits and concatenate.
 */
async function fetchAllDirectDebits(token, onProgress) {
  const params = new URLSearchParams(window.location.search)
  let maxExecutionDate = params.get('maxExecutionDate')
  if (!maxExecutionDate) {
    const d = new Date()
    d.setMonth(d.getMonth() + 1)
    d.setDate(0)
    maxExecutionDate = d.toISOString().slice(0, 10) + 'T23:59:59.999Z'
  }
  const order = params.get('order') || 'ASC'
  const sort = params.get('sort') || 'propertyIdInternal'

  const all = []
  let page = 0
  let totalPages = 1
  const defaultPageSize = 30

  while (true) {
    if (onProgress) onProgress({ page: page + 1, totalPages, loaded: all.length })
    const data = await fetchDirectDebitsPage(token, maxExecutionDate, order, sort, page)
    const chunk = Array.isArray(data.content) ? data.content : []
    for (const item of chunk) all.push(item)
    const size = typeof data.size === 'number' ? data.size : defaultPageSize
    if (typeof data.totalPages === 'number') totalPages = data.totalPages
    if (chunk.length === 0 || chunk.length < size) break
    page++
    if (page >= totalPages) break
  }
  return all
}

/**
 * Group items first by property, then by counterpartName + counterpartIban.
 * Sammel-Lastschrift requires all selected items to be from one property.
 */
function groupByPropertyThenCounterpart(items) {
  const byProperty = new Map()
  for (const item of items) {
    const propId = item.propertyId != null ? item.propertyId : ''
    const propName = item.propertyName || item.propertyIdInternal || String(propId)
    if (!byProperty.has(propId)) {
      byProperty.set(propId, { propertyId: propId, propertyName: propName, counterpartGroups: new Map() })
    }
    const propGroup = byProperty.get(propId)
    const name = item.counterpartName || ''
    const iban = item.counterpartIban || ''
    const cKey = `${name}|${iban}`
    if (!propGroup.counterpartGroups.has(cKey)) {
      propGroup.counterpartGroups.set(cKey, { counterpartName: name, counterpartIban: iban, items: [] })
    }
    propGroup.counterpartGroups.get(cKey).items.push(item)
  }
  return Array.from(byProperty.values()).map((p) => ({
    propertyId: p.propertyId,
    propertyName: p.propertyName,
    counterpartGroups: Array.from(p.counterpartGroups.values())
  }))
}

/**
 * Build and show the Sammel-Lastschrift dialog (black/white UI).
 */
function openSammelDialog() {
  const auth = getAuth()
  if (!auth) {
    alert('Nicht angemeldet oder Domain konnte nicht ermittelt werden.')
    return
  }

  const overlay = document.createElement('div')
  overlay.id = 'lux-sammel-dialog-overlay'
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.45);z-index:9998;display:flex;align-items:center;justify-content:center;'
  document.body.appendChild(overlay)

  const dialog = document.createElement('div')
  dialog.id = 'lux-sammel-dialog'
  dialog.style.cssText = 'background:#fff;border:1px solid #000;border-radius:4px;min-width:640px;max-width:90vw;max-height:85vh;display:flex;flex-direction:column;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.15);'
  overlay.appendChild(dialog)

  const header = document.createElement('div')
  header.style.cssText = 'padding:16px;border-bottom:1px solid #000;display:flex;justify-content:space-between;align-items:center;'
  header.innerHTML = '<span style="font-weight:600;font-size:16px;color:#000;">Lastschriften zusammenfassen (Sammel-Lastschrift)</span><button type="button" class="lux-sammel-close" style="background:none;border:none;cursor:pointer;font-size:20px;line-height:1;color:#000;">×</button>'
  dialog.appendChild(header)

  const content = document.createElement('div')
  content.style.cssText = 'padding:16px;overflow:auto;flex:1;'
  content.innerHTML = '<p style="color:#000;margin:0 0 12px;">Lade Lastschriften…</p>'
  dialog.appendChild(content)

  const footer = document.createElement('div')
  footer.style.cssText = 'padding:16px;border-top:1px solid #000;display:flex;justify-content:space-between;align-items:center;gap:8px;'
  const footerLeft = document.createElement('div')
  footerLeft.style.cssText = 'display:flex;gap:8px;align-items:center;'
  const btnArchiv = document.createElement('button')
  btnArchiv.type = 'button'
  btnArchiv.className = 'ant-btn ant-btn-ghost'
  btnArchiv.textContent = 'Archiv'
  btnArchiv.style.cssText = 'color:#000;border-color:#000;'
  btnArchiv.addEventListener('click', () => {
    close()
    openArchiveDialog(() => openSammelDialog())
  })
  footerLeft.appendChild(btnArchiv)
  const selectionSummary = document.createElement('span')
  selectionSummary.id = 'lux-sammel-selection-summary'
  selectionSummary.style.cssText = 'font-size:13px;font-weight:600;color:#000;margin-left:4px;'
  footerLeft.appendChild(selectionSummary)
  const footerRight = document.createElement('div')
  footerRight.style.cssText = 'display:flex;gap:8px;'
  const btnCancel = document.createElement('button')
  btnCancel.type = 'button'
  btnCancel.className = 'ant-btn ant-btn-ghost'
  btnCancel.textContent = 'Abbrechen'
  btnCancel.style.cssText = 'color:#000;border-color:#000;'
  const btnSammelVwz = document.createElement('button')
  btnSammelVwz.type = 'button'
  btnSammelVwz.className = 'ant-btn ant-btn-default'
  btnSammelVwz.textContent = 'Sammel-Lastschrift (abw. VWZ)'
  btnSammelVwz.style.cssText = 'color:#000;border-color:#000;'
  btnSammelVwz.disabled = true
  const btnSammel = document.createElement('button')
  btnSammel.type = 'button'
  btnSammel.className = 'ant-btn'
  btnSammel.textContent = 'Sammel-Lastschrift'
  btnSammel.style.cssText = 'background:#000;border-color:#000;color:#fff;'
  btnSammel.disabled = true
  footerRight.appendChild(btnCancel)
  footerRight.appendChild(btnSammelVwz)
  footerRight.appendChild(btnSammel)
  footer.appendChild(footerLeft)
  footer.appendChild(footerRight)
  dialog.appendChild(footer)

  const close = () => {
    overlay.remove()
  }
  header.querySelector('.lux-sammel-close').addEventListener('click', close)
  btnCancel.addEventListener('click', close)
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close() })

  const selectedIds = new Set()
  let allItems = []

  function updateSammelButton() {
    const disabled = selectedIds.size === 0
    btnSammel.disabled = disabled
    btnSammelVwz.disabled = disabled
    const selectAll = content.querySelector('#lux-sammel-select-all')
    if (selectAll && displayItemsRef.length > 0) {
      selectAll.checked = displayItemsRef.every((item) => selectedIds.has(item.id))
    }
    if (selectedIds.size === 0) {
      selectionSummary.textContent = ''
    } else {
      const sum = allItems
        .filter((item) => selectedIds.has(item.id))
        .reduce((s, item) => s + (Number(item.amount) || 0), 0)
      selectionSummary.textContent = `${selectedIds.size} ausgewählt · ${formatAmount(sum)}`
    }
  }

  /** True if item matches search string (case-insensitive). */
  function itemMatchesSearch(item, q) {
    if (!q || !q.trim()) return true
    const lower = q.trim().toLowerCase()
    const prop = (item.propertyName || item.propertyIdInternal || item.propertyId || '').toString()
    const name = (item.counterpartName || '').toString()
    const iban = (item.counterpartIban || '').toString()
    const stmt = (item.statement || '').toString()
    const date = (item.executionDate || '').toString()
    const amt = formatAmount(item.amount)
    return prop.toLowerCase().includes(lower) || name.toLowerCase().includes(lower) ||
      iban.toLowerCase().includes(lower) || stmt.toLowerCase().includes(lower) ||
      date.toLowerCase().includes(lower) || amt.toLowerCase().includes(lower)
  }

  let tableWrap = null
  let displayItemsRef = []

  function renderTable() {
    if (!tableWrap) return
    const searchQuery = content.querySelector('.lux-sammel-search')?.value ?? ''
    const displayItems = allItems.filter((item) => itemMatchesSearch(item, searchQuery))
    displayItemsRef = displayItems
    const propertyGroups = groupByPropertyThenCounterpart(displayItems)
    let html = '<table style="width:100%;border-collapse:collapse;color:#000;font-size:13px;">'
    html += '<thead><tr style="border-bottom:2px solid #000;text-align:left;">'
    html += '<th style="padding:8px;width:28px;"><input type="checkbox" id="lux-sammel-select-all" title="Alle auswählen" /></th>'
    html += '<th style="padding:8px;">Objekt</th><th style="padding:8px;">Eigentümer / IBAN</th><th style="padding:8px;">Datum</th><th style="padding:8px;">Betrag</th><th style="padding:8px;">Verwendungszweck</th></tr></thead><tbody>'
    for (const propGroup of propertyGroups) {
      html += `<tr class="lux-sammel-property-row" style="background:#f0f0f0;border-bottom:1px solid #000;"><td colspan="6" style="padding:10px 8px;font-weight:700;">Objekt: ${escapeHtml(propGroup.propertyName)}</td></tr>`
      for (const counterpart of propGroup.counterpartGroups) {
        const ownerIds = counterpart.items.map((i) => i.id)
        const ownerIdsStr = ownerIds.join(',')
        const sum = counterpart.items.reduce((s, i) => s + (Number(i.amount) || 0), 0)
        const allOwnerSelected = ownerIds.every((id) => selectedIds.has(id))
        const ownerChecked = allOwnerSelected ? ' checked' : ''
        html += `<tr class="lux-sammel-owner-row" style="background:#e8e8e8;border-bottom:1px solid #ccc;">`
        html += `<td style="padding:8px;"><input type="checkbox" class="lux-sammel-select-owner" data-owner-ids="${escapeHtml(ownerIdsStr)}"${ownerChecked} title="Alle Lastschriften dieses Eigentümers auswählen" /></td>`
        html += `<td style="padding:8px;"></td>`
        html += `<td style="padding:8px;font-weight:600;">${escapeHtml(counterpart.counterpartName)} / ${escapeHtml(counterpart.counterpartIban)}</td>`
        html += `<td style="padding:8px;">${counterpart.items.length} Positionen</td>`
        html += `<td style="padding:8px;">${formatAmount(sum)}</td>`
        html += `<td style="padding:8px;"><em>Alle für diesen Eigentümer auswählen</em></td></tr>`
        for (const item of counterpart.items) {
          const id = item.id
          const checked = selectedIds.has(id) ? ' checked' : ''
          html += `<tr style="border-bottom:1px solid #d9d9d9;" data-id="${id}">`
          html += `<td style="padding:8px;"><input type="checkbox" class="lux-sammel-row" data-id="${id}"${checked} /></td>`
          html += `<td style="padding:8px;">${escapeHtml(propGroup.propertyName)}</td>`
          html += `<td style="padding:8px;">${escapeHtml(counterpart.counterpartName)} / ${escapeHtml(counterpart.counterpartIban)}</td>`
          html += `<td style="padding:8px;">${escapeHtml(formatDateGerman(item.executionDate))}</td>`
          html += `<td style="padding:8px;">${formatAmount(item.amount)}</td>`
          html += `<td style="padding:8px;">${escapeHtml((item.statement || '').slice(0, 60))}</td>`
          html += '</tr>'
        }
      }
    }
    html += '</tbody></table>'
    tableWrap.innerHTML = html

    content.querySelector('#lux-sammel-select-all')?.addEventListener('change', (e) => {
      const checked = e.target.checked
      displayItemsRef.forEach((item) => (checked ? selectedIds.add(item.id) : selectedIds.delete(item.id)))
      content.querySelectorAll('.lux-sammel-row').forEach((cb) => { cb.checked = checked })
      content.querySelectorAll('.lux-sammel-select-owner').forEach((cb) => { cb.checked = checked })
      updateSammelButton()
    })
    content.querySelectorAll('.lux-sammel-select-owner').forEach((cb) => {
      cb.addEventListener('change', (e) => {
        const ids = e.target.dataset.ownerIds.split(',').map((s) => Number(s.trim())).filter(Boolean)
        if (e.target.checked) ids.forEach((id) => selectedIds.add(id))
        else ids.forEach((id) => selectedIds.delete(id))
        content.querySelectorAll('.lux-sammel-row').forEach((rowCb) => {
          const id = Number(rowCb.dataset.id)
          if (ids.includes(id)) rowCb.checked = e.target.checked
        })
        updateSammelButton()
      })
    })
    content.querySelectorAll('.lux-sammel-row').forEach((cb) => {
      cb.addEventListener('change', (e) => {
        const id = Number(e.target.dataset.id)
        if (e.target.checked) selectedIds.add(id)
        else selectedIds.delete(id)
        content.querySelectorAll('.lux-sammel-select-owner').forEach((ownerCb) => {
          const ids = ownerCb.dataset.ownerIds.split(',').map((s) => Number(s.trim())).filter(Boolean)
          ownerCb.checked = ids.every((oid) => selectedIds.has(oid))
        })
        updateSammelButton()
      })
    })
  }

  const contentDiv = content
  fetchAllDirectDebits(auth.token, (p) => {
    contentDiv.innerHTML = `<p style="color:#000;">Lade Lastschriften… Seite ${p.page}${p.totalPages > 1 ? ' von ' + p.totalPages : ''} (${p.loaded} Einträge)</p>`
  })
    .then((items) => {
      allItems = items
      if (allItems.length === 0) {
        contentDiv.innerHTML = '<p style="color:#000;">Keine Lastschriften (NEW) gefunden.</p>'
        return
      }
      contentDiv.innerHTML = ''
      const searchWrap = document.createElement('div')
      searchWrap.style.cssText = 'margin-bottom:12px;flex-shrink:0;'
      searchWrap.innerHTML = '<input type="text" class="lux-sammel-search" placeholder="Suchen…" style="width:100%;max-width:320px;padding:8px 12px;border:1px solid #000;border-radius:4px;font-size:13px;color:#000;" />'
      contentDiv.appendChild(searchWrap)
      tableWrap = document.createElement('div')
      tableWrap.className = 'lux-sammel-table-wrap'
      tableWrap.style.cssText = 'min-height:0;overflow:auto;'
      contentDiv.appendChild(tableWrap)
      const searchInput = contentDiv.querySelector('.lux-sammel-search')
      if (searchInput) {
        searchInput.addEventListener('input', () => renderTable())
        searchInput.addEventListener('keydown', (e) => e.stopPropagation())
      }
      renderTable()
    })
    .catch((err) => {
      contentDiv.innerHTML = '<p style="color:#000;">Fehler beim Laden: ' + escapeHtml(err.message) + '</p>'
    })

  async function submitSammel(customStatement) {
    if (selectedIds.size === 0) return
    const selected = allItems.filter(item => selectedIds.has(item.id))
    const propertyIds = [...new Set(selected.map(item => item.propertyId))]
    const counterpartKeys = [...new Set(selected.map(item => `${item.counterpartName || ''}|${item.counterpartIban || ''}`))]
    if (propertyIds.length > 1) {
      alert('Alle ausgewählten Lastschriften müssen vom selben Objekt stammen. Bitte wählen Sie nur Lastschriften eines Objekts aus.')
      return
    }
    if (counterpartKeys.length > 1) {
      alert('Alle ausgewählten Lastschriften müssen dasselbe Gegenkonto (Name + IBAN) haben. Bitte wählen Sie nur Lastschriften eines Gegenkontos aus.')
      return
    }
    const body = { selectedItems: selected, domainId: auth.domainId }
    if (customStatement != null && String(customStatement).trim() !== '') {
      body.statement = String(customStatement).trim()
    }
    btnSammel.disabled = true
    btnSammelVwz.disabled = true
    btnSammel.textContent = 'Wird ausgeführt…'
    btnSammelVwz.textContent = 'Wird ausgeführt…'
    try {
      const res = await fetch('/api/direct-debits/sammel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`
        },
        body: JSON.stringify(body)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(data.error || data.message || `Fehler: ${res.status}`)
      }
      close()
      window.location.reload()
    } catch (err) {
      alert(err.message || 'Fehler beim Zusammenfassen.')
      btnSammel.disabled = false
      btnSammelVwz.disabled = selectedIds.size === 0
      btnSammel.textContent = 'Sammel-Lastschrift'
      btnSammelVwz.textContent = 'Sammel-Lastschrift (abw. VWZ)'
    }
  }

  btnSammelVwz.addEventListener('click', () => {
    if (selectedIds.size === 0) return
    const vwz = prompt('Verwendungszweck für die Sammel-Lastschrift:', '')
    if (vwz === null) return
    submitSammel(vwz)
  })

  btnSammel.addEventListener('click', () => submitSammel())
}

/**
 * Format amount for display; direct debit .amount is in euros (not cents).
 */
function formatAmount(n) {
  const num = Number(n)
  if (Number.isNaN(num)) return ''
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(num)
}

/** Format date string (ISO or similar) to German format: DD.MM.YYYY (e.g. 23.02.2025). */
function formatDateGerman(dateStr) {
  if (dateStr == null || String(dateStr).trim() === '') return ''
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return String(dateStr).trim()
  return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date)
}

/**
 * Open the Archiv dialog: list of aggregations for this domain (same black/white style as Sammel).
 * @param {() => void} [onBack] - Called when "Zurück" is clicked; e.g. re-open Sammel dialog to return to list of single direct debits.
 */
function openArchiveDialog(onBack) {
  const auth = getAuth()
  if (!auth) {
    alert('Nicht angemeldet oder Domain konnte nicht ermittelt werden.')
    return
  }

  const overlay = document.createElement('div')
  overlay.id = 'lux-archiv-dialog-overlay'
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.45);z-index:9998;display:flex;align-items:center;justify-content:center;'
  document.body.appendChild(overlay)

  const dialog = document.createElement('div')
  dialog.id = 'lux-archiv-dialog'
  dialog.style.cssText = 'background:#fff;border:1px solid #000;border-radius:4px;min-width:640px;max-width:90vw;max-height:85vh;display:flex;flex-direction:column;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.15);'
  overlay.appendChild(dialog)

  const header = document.createElement('div')
  header.style.cssText = 'padding:16px;border-bottom:1px solid #000;display:flex;justify-content:space-between;align-items:center;'
  header.innerHTML = '<span style="font-weight:600;font-size:16px;color:#000;">Archiv – Sammel-Lastschriften</span><div style="display:flex;gap:8px;align-items:center;"><button type="button" class="lux-archiv-back" style="background:#000;border:1px solid #000;color:#fff;padding:6px 12px;border-radius:4px;cursor:pointer;font-size:13px;">Zurück</button><button type="button" class="lux-archiv-close" style="background:none;border:none;cursor:pointer;font-size:20px;line-height:1;color:#000;">×</button></div>'
  dialog.appendChild(header)

  const contentWrapper = document.createElement('div')
  contentWrapper.style.cssText = 'padding:16px;overflow:auto;flex:1;display:flex;flex-direction:column;gap:12px;'
  contentWrapper.innerHTML = '<p style="color:#000;margin:0;">Lade Archiv…</p>'
  dialog.appendChild(contentWrapper)

  const close = () => overlay.remove()
  header.querySelector('.lux-archiv-back').addEventListener('click', () => {
    close()
    if (typeof onBack === 'function') onBack()
  })
  header.querySelector('.lux-archiv-close').addEventListener('click', () => {
    close()
    if (typeof onBack === 'function') onBack()
  })
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      close()
      if (typeof onBack === 'function') onBack()
    }
  })

  /** Returns true if aggregation or any of its transactions match the search string (case-insensitive). */
  function aggregationMatchesSearch(row, q) {
    if (!q || !q.trim()) return true
    const lower = q.trim().toLowerCase()
    const items = Array.isArray(row.directDebits) ? row.directDebits : []
    const sum = items.reduce((s, i) => s + (Number(i.amount) || 0), 0)
    const sumStr = formatAmount(sum)
    if (String(row.id).toLowerCase().includes(lower)) return true
    if (String(row.aggregatedDirectDebitId).toLowerCase().includes(lower)) return true
    if (sumStr.toLowerCase().includes(lower)) return true
    for (const t of items) {
      const prop = (t.propertyName || t.propertyIdInternal || t.propertyId || '').toString()
      const name = (t.counterpartName || '').toString()
      const iban = (t.counterpartIban || '').toString()
      const stmt = (t.statement || '').toString()
      const date = (t.executionDate || '').toString()
      const amt = formatAmount(t.amount)
      if (prop.toLowerCase().includes(lower) || name.toLowerCase().includes(lower) ||
          iban.toLowerCase().includes(lower) || stmt.toLowerCase().includes(lower) ||
          date.toLowerCase().includes(lower) || amt.toLowerCase().includes(lower)) return true
    }
    return false
  }

  function renderArchiveList(data, searchQuery, listContainer) {
    if (!listContainer) return
    if (!Array.isArray(data) || data.length === 0) {
      listContainer.innerHTML = '<p style="color:#000;margin:0;">Keine Sammel-Lastschriften im Archiv.</p>'
      return
    }
    const filtered = data.filter((row) => aggregationMatchesSearch(row, searchQuery))
    if (filtered.length === 0) {
      listContainer.innerHTML = '<p style="color:#000;margin:0;">Keine Einträge passen zur Suche.</p>'
      return
    }
    let html = ''
    for (const row of filtered) {
      const items = Array.isArray(row.directDebits) ? row.directDebits : []
      const sum = items.reduce((s, i) => s + (Number(i.amount) || 0), 0)
      html += '<section class="lux-archiv-aggregation" style="margin-bottom:20px;border:1px solid #000;border-radius:4px;overflow:hidden;">'
      html += '<div style="padding:10px 12px;background:#f0f0f0;border-bottom:1px solid #000;font-weight:700;font-size:13px;color:#000;">'
      html += `Aggregation #${escapeHtml(String(row.id))} → Lastschrift #${escapeHtml(String(row.aggregatedDirectDebitId))} · ${items.length} Positionen · Summe ${formatAmount(sum)}`
      html += '</div>'
      html += '<table style="width:100%;border-collapse:collapse;color:#000;font-size:13px;">'
      html += '<thead><tr style="border-bottom:2px solid #000;text-align:left;background:#e8e8e8;">'
      html += '<th style="padding:8px;">Objekt</th><th style="padding:8px;">Eigentümer / IBAN</th><th style="padding:8px;">Datum</th><th style="padding:8px;">Betrag</th><th style="padding:8px;">Verwendungszweck</th></tr></thead><tbody>'
      for (const t of items) {
        const prop = escapeHtml((t.propertyName || t.propertyIdInternal || t.propertyId || '').toString())
        const name = escapeHtml((t.counterpartName || '').toString())
        const iban = escapeHtml((t.counterpartIban || '').toString())
        const date = escapeHtml(formatDateGerman(t.executionDate))
        const stmt = escapeHtml((t.statement || '').toString().slice(0, 80))
        html += '<tr style="border-bottom:1px solid #d9d9d9;">'
        html += `<td style="padding:8px;">${prop}</td>`
        html += `<td style="padding:8px;">${name} / ${iban}</td>`
        html += `<td style="padding:8px;">${date}</td>`
        html += `<td style="padding:8px;">${formatAmount(t.amount)}</td>`
        html += `<td style="padding:8px;">${stmt}</td>`
        html += '</tr>'
      }
      html += '</tbody></table></section>'
    }
    listContainer.innerHTML = html
  }

  fetch(`/api/direct-debits/aggregations?domainId=${encodeURIComponent(auth.domainId)}`, {
    method: 'GET',
    headers: { accept: 'application/json', Authorization: `Bearer ${auth.token}` }
  })
    .then(async (res) => {
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        return { error: data.error || data.message || `Fehler: ${res.status}` }
      }
      return data
    })
    .then((data) => {
      if (data && data.error) {
        contentWrapper.innerHTML = '<p style="color:#000;">Fehler beim Laden: ' + escapeHtml(data.error) + '</p>'
        return
      }
      if (!Array.isArray(data)) {
        contentWrapper.innerHTML = '<p style="color:#000;">Fehler beim Laden: Unbekannter Fehler</p>'
        return
      }
      contentWrapper.innerHTML = ''
      const searchWrap = document.createElement('div')
      searchWrap.style.cssText = 'flex-shrink:0;'
      searchWrap.innerHTML = '<input type="text" class="lux-archiv-search" placeholder="Suchen…" style="width:100%;max-width:320px;padding:8px 12px;border:1px solid #000;border-radius:4px;font-size:13px;color:#000;" />'
      contentWrapper.appendChild(searchWrap)
      const listEl = document.createElement('div')
      listEl.className = 'lux-archiv-list'
      listEl.style.cssText = 'flex:1;min-height:0;overflow:auto;'
      contentWrapper.appendChild(listEl)
      let archiveData = data
      renderArchiveList(archiveData, '', listEl)
      const searchInput = contentWrapper.querySelector('.lux-archiv-search')
      if (searchInput) {
        searchInput.addEventListener('input', () => {
          renderArchiveList(archiveData, searchInput.value, listEl)
        })
        searchInput.addEventListener('keydown', (e) => { e.stopPropagation() })
      }
    })
    .catch((err) => {
      contentWrapper.innerHTML = '<p style="color:#000;">Fehler beim Laden: ' + escapeHtml(err.message || 'Netzwerkfehler') + '</p>'
    })
}

function Lux_initializeDirectDebitSammelButton() {
  const containerSelector = '.DirectDebitListHeaderButtons'
  const buttonId = LUX_SAMMEL_BUTTON_ID

  if (Lux_DirectDebitSammel_observer) {
    Lux_DirectDebitSammel_observer.disconnect()
    Lux_DirectDebitSammel_observer = null
  }

  function injectButton() {
    const container = document.querySelector(containerSelector)
    if (!container || document.getElementById(buttonId)) return

    const firstChild = container.firstElementChild
    if (!firstChild) return

    const newButton = firstChild.cloneNode(true)
    newButton.id = buttonId
    newButton.classList.remove('ant-dropdown-trigger', 'drop-menu-button', 'drop-menu')
    newButton.setAttribute('type', 'button')
    newButton.removeAttribute('aria-haspopup')
    newButton.removeAttribute('aria-expanded')
    const iconSpan = newButton.querySelector('.anticon, [class*="drop-menu"]')
    if (iconSpan) {
      iconSpan.className = 'anticon lux-sammel-link-icon'
      iconSpan.innerHTML = CHAIN_LINK_ICON_SVG
    }
    newButton.querySelector('.ant-dropdown-trigger')?.classList.remove('ant-dropdown-trigger')
    newButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      openSammelDialog()
    })
    container.insertBefore(newButton, container.firstChild)
  }

  const container = document.querySelector(containerSelector)
  if (container) {
    injectButton()
    return
  }

  Lux_DirectDebitSammel_observer = new MutationObserver(() => {
    if (document.querySelector(containerSelector)) {
      Lux_DirectDebitSammel_observer.disconnect()
      Lux_DirectDebitSammel_observer = null
      injectButton()
    }
  })
  Lux_DirectDebitSammel_observer.observe(document.body, { childList: true, subtree: true })
}
